/**
 * <s-product-gallery> — product photo gallery.
 *
 * The photos sit in one horizontal scroll track ([data-product-gallery]) with scroll-snap,
 * so swiping/trackpad scrolling works natively. Thumbnails ([data-gallery-thumb], desktop)
 * and dots ([data-gallery-dot], mobile) just ask the browser to smooth-scroll the track to
 * that photo — the same native motion as a swipe. The thumbnail/dot for the photo in view
 * is marked (aria-pressed / aria-current) as the track scrolls.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #frame = 0;
  #moving = false;
  #targetLeft = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.addEventListener('command', this.#onThumb, { signal });
    this.addEventListener('click', this.#onDot, { signal });
    this.#track?.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    this.#track?.addEventListener('scrollend', () => { this.#moving = false; this.#onScroll(); }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #track() {
    return this.querySelector('[data-product-gallery]');
  }

  // Only the slides shown at this screen size (mobile and desktop can show different photos).
  get #slides() {
    return [...this.querySelectorAll('[data-gallery-slide]')].filter((slide) => slide.getClientRects().length > 0);
  }

  // Position of a slide inside the track, measured from the layout (not assumed from the index).
  #offsetOf(slide) {
    const track = this.#track;
    return track.scrollLeft + slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
  }

  #goTo(index) {
    const track = this.#track;
    const slide = this.#slides[index];
    if (!track || !slide) return;
    this.#mark(index);
    // Keep the clicked thumbnail selected while the track travels past the photos in between.
    this.#targetLeft = this.#offsetOf(slide);
    this.#moving = Math.abs(track.scrollLeft - this.#targetLeft) > 1;
    track.scrollTo({ left: this.#offsetOf(slide), behavior: 'smooth' });
  }

  #onThumb = (event) => {
    if (event.command !== '--show-media') return;
    const mediaId = event.source?.dataset.mediaId;
    this.#goTo(this.#slides.findIndex((slide) => slide.dataset.mediaId === mediaId));
  };

  #onDot = (event) => {
    const dot = event.target.closest('[data-gallery-dot]');
    if (dot && this.contains(dot)) this.#goTo(Number(dot.dataset.index));
  };

  #onScroll = () => {
    // Browsers without scrollend: release once the track reaches the target.
    if (this.#moving && Math.abs(this.#track.scrollLeft - this.#targetLeft) <= 1) this.#moving = false;
    if (this.#moving) return;
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const track = this.#track;
      if (!track) return;
      // The photo in view is the one whose left edge is closest to the track's current position.
      let best = 0;
      let bestDistance = Infinity;
      this.#slides.forEach((slide, i) => {
        const distance = Math.abs(this.#offsetOf(slide) - track.scrollLeft);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      this.#mark(best);
    });
  };

  #mark(index) {
    const mediaId = this.#slides[index]?.dataset.mediaId;
    if (!mediaId) return;
    this.#slides.forEach((slide, i) => slide.toggleAttribute('data-active', i === index));
    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      thumb.setAttribute('aria-pressed', String(thumb.dataset.mediaId === mediaId));
    });
    this.querySelectorAll('[data-gallery-dot]').forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
